import React, { useState, useRef } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { GHARS, GHAR_TYPE_HI, applyTitle, splitTitle } from "../../data/rssbData";
import { hinglishToHindi } from "../../lib/translit";
import { HindiInput } from "../HindiInput";
import { TitlePicker } from "../TitlePicker";
import { parseSpecialFile, uid } from "../../lib/specialSkParser";
import { toast } from "sonner";
import { UploadCloud, Trash2, Plus, Star, Lock, Pencil } from "lucide-react";

const GharSelect = ({ value, onChange, testid }) => (
  <select
    data-testid={testid}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className="w-full bg-[#f6eeda] border border-[#e3d6b6] rounded-md px-3 py-2 text-sm text-[#3a1212]"
  >
    <option value="">— सत्संग घर चुनें —</option>
    {GHARS.map((g) => (
      <option key={g.id} value={g.id}>
        {g.hi}–{GHAR_TYPE_HI[g.type]}
      </option>
    ))}
  </select>
);

export const SpecialSkTab = ({ entries, setEntries, year, month }) => {
  const empty = { dateIso: "", skName: "", title: "श्री", morningGharId: "", noonGharId: "", fromCity: "", skMobile: "" };
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addManual = async () => {
    if (!form.dateIso || !form.skName) {
      toast.error("तारीख और SK नाम आवश्यक हैं");
      return;
    }
    if (!form.morningGharId && !form.noonGharId) {
      toast.error("कम से कम एक सत्संग घर (प्रातः या दोपहर) चुनें");
      return;
    }
    const dow = new Date(form.dateIso + "T00:00:00").getDay();
    if (![0, 3, 4].includes(dow)) {
      toast.warning("चुनी गई तारीख रविवार/बुधवार/गुरुवार नहीं है — यह शेड्यूल कॉलम में नहीं दिखेगी");
    }
    const { title, ...rest } = form;
    const skName = applyTitle(title, await hinglishToHindi(form.skName.trim()));
    const fromCity = form.fromCity.trim() ? await hinglishToHindi(form.fromCity.trim()) : "";
    const entry = { ...rest, skName, fromCity, skMobile: (form.skMobile || "").trim() };
    if (editingId) {
      setEntries(entries.map((e) => (e.id === editingId ? { ...e, ...entry, morningGharText: "", noonGharText: "" } : e)));
      toast.success("विशेष SK एंट्री अपडेट हुई");
    } else {
      setEntries([...entries, { ...entry, id: uid() }]);
      toast.success("विशेष SK एंट्री जोड़ी गई");
    }
    setForm(empty);
    setEditingId(null);
  };

  const startEdit = (e) => {
    const s = splitTitle(e.skName);
    setForm({
      dateIso: e.dateIso || "", skName: s.rest, title: s.title || "श्री", morningGharId: e.morningGharId || "",
      noonGharId: e.noonGharId || "", fromCity: e.fromCity || "", skMobile: e.skMobile || "",
    });
    setEditingId(e.id);
    document.querySelector('[data-testid="special-form"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const remove = (id) => setEntries(entries.filter((e) => e.id !== id));

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const parsed = await parseSpecialFile(file, year, month);
      if (!parsed.length) {
        toast.error("फ़ाइल से कोई एंट्री नहीं पढ़ी जा सकी — कृपया कॉलम जाँचें या मैनुअल जोड़ें");
      } else {
        // A file is the authoritative special-SK list for the month → replace, don't append.
        setEntries(parsed);
        const unmatched = parsed.filter((e) => (e.morningGharText && !e.morningGharId) || (e.noonGharText && !e.noonGharId)).length;
        toast.success(`${parsed.length} विशेष SK एंट्री पढ़ी व लॉक की गईं${unmatched ? ` (${unmatched} घर मैच नहीं हुए — सूची में जाँचें)` : ""}`);
      }
    } catch (err) {
      toast.error("फ़ाइल पढ़ने में त्रुटि: " + err.message);
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const gharName = (id) => GHARS.find((g) => g.id === id)?.hi || "—";

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <div className="space-y-4">
        <div
          data-testid="special-dropzone"
          onClick={() => fileRef.current?.click()}
          className="border-2 border-dashed border-[#6e1b1b]/40 rounded-xl p-8 text-center cursor-pointer hover:border-[#6e1b1b] hover:bg-[#6e1b1b]/5 transition-all"
        >
          <UploadCloud className="w-10 h-10 mx-auto text-[#6e1b1b] mb-2" />
          <p className="font-serif-dev text-[#3a1212]">विशेष SK शेड्यूल अपलोड करें</p>
          <p className="text-xs text-[#7a4a3a] mt-1">Excel कॉलम: Date · Satsang Karta Name · Contact No. · Time (9:30/1:00) · Satsang Ghar · From (Area)</p>
          <p className="text-[11px] text-[#9a7a5a] mt-0.5">अपलोड करने पर यह सूची महीने की फिक्स्ड विशेष सूची मानी जाएगी (पुरानी एंट्री बदल जाएँगी)</p>
          {busy && <p className="text-xs text-[#6e1b1b] mt-2 animate-pulse">पढ़ा जा रहा है...</p>}
          <input ref={fileRef} type="file" accept=".pdf,.xlsx,.xls,.csv" onChange={onFile} className="hidden" data-testid="special-file-input" />
        </div>

        <div data-testid="special-form" className={`bg-[#fffdf7] border rounded-xl p-4 space-y-3 scroll-mt-24 ${editingId ? "border-[#6e1b1b] ring-2 ring-[#6e1b1b]/30" : "border-[#e3d6b6]"}`}>
          <h3 className="font-serif-dev font-semibold text-[#3a1212] flex items-center gap-2">
            {editingId ? <Pencil className="w-4 h-4 text-[#6e1b1b]" /> : <Plus className="w-4 h-4 text-[#6e1b1b]" />}
            {editingId ? "एंट्री बदलें" : "मैनुअल एंट्री"}
          </h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-[#7a4a3a]">तारीख</Label>
              <Input type="date" data-testid="special-date" value={form.dateIso} onChange={(e) => upd("dateIso", e.target.value)} className="bg-[#f6eeda] border-[#e3d6b6] text-[#3a1212]" />
            </div>
            <div>
              <Label className="text-xs text-[#7a4a3a]">SK नाम</Label>
              <TitlePicker testid="special-title" value={form.title} onChange={(v) => upd("title", v)} />
            </div>
          </div>
          <HindiInput testid="special-name" value={form.skName} onChange={(v) => upd("skName", v)} placeholder="SK नाम — e.g. Rahul Verma" className="bg-[#f6eeda] border-[#e3d6b6] text-[#3a1212] font-dev" />
          <div>
            <Label className="text-xs text-[#7a4a3a]">प्रातः घर (09:30)</Label>
            <GharSelect testid="special-morning" value={form.morningGharId} onChange={(v) => upd("morningGharId", v)} />
          </div>
          <div>
            <Label className="text-xs text-[#7a4a3a]">दोपहर घर (13:00)</Label>
            <GharSelect testid="special-noon" value={form.noonGharId} onChange={(v) => upd("noonGharId", v)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-[#7a4a3a]">किस शहर से</Label>
              <HindiInput testid="special-city" value={form.fromCity} onChange={(v) => upd("fromCity", v)} placeholder="मुरादाबाद / पानीपत ..." className="bg-[#f6eeda] border-[#e3d6b6] text-[#3a1212] font-dev" />
            </div>
            <div>
              <Label className="text-xs text-[#7a4a3a]">मोबाइल</Label>
              <Input data-testid="special-mobile" inputMode="tel" value={form.skMobile} onChange={(e) => upd("skMobile", e.target.value)} className="bg-[#f6eeda] border-[#e3d6b6] text-[#3a1212]" />
            </div>
          </div>
          <div className="flex gap-2">
            <Button data-testid="special-add" onClick={addManual} className="flex-1 bg-[#6e1b1b] text-[#fbf6ea] hover:bg-[#7a1f1f] font-semibold">
              {editingId ? "बदलाव सहेजें" : "एंट्री जोड़ें"}
            </Button>
            {editingId && (
              <Button data-testid="special-edit-cancel" variant="outline" onClick={() => { setEditingId(null); setForm(empty); }} className="border-[#6e1b1b]/40 text-[#5e1414]">
                रद्द करें
              </Button>
            )}
          </div>
        </div>
      </div>

      <div>
        <h3 className="font-serif-dev font-semibold text-[#3a1212] mb-3 flex items-center gap-2">
          <Lock className="w-4 h-4 text-[#6e1b1b]" /> लॉक की गई विशेष SK एंट्री ({entries.length})
        </h3>
        <div className="space-y-2" data-testid="special-list">
          {entries.map((e) => (
            <div key={e.id} className="bg-[#f6eeda] border border-[#6e1b1b]/40 rounded-lg p-3 animate-fade-up shadow-[inset_0_0_10px_rgba(94,20,20,0.08)]">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 font-serif-dev font-semibold text-[#3a1212]">
                    <Star className="w-3.5 h-3.5 fill-[#6e1b1b] text-[#6e1b1b]" />
                    {e.skName || "(नाम नहीं)"}
                  </div>
                  <div className="text-xs text-[#7a4a3a] mt-1 font-dev">
                    📅 {e.dateIso || "?"} · 🏙 {e.fromCity || "—"}{e.skMobile ? ` · 📞 ${e.skMobile}` : ""}
                  </div>
                  <div className="text-xs text-[#7a4a3a] mt-1 font-dev">
                    प्रातः: {gharName(e.morningGharId) }{e.morningGharText && !e.morningGharId ? ` (${e.morningGharText})` : ""} · दोपहर: {gharName(e.noonGharId)}{e.noonGharText && !e.noonGharId ? ` (${e.noonGharText})` : ""}
                  </div>
                </div>
                <div className="flex shrink-0">
                  <button data-testid={`special-edit-${e.id}`} onClick={() => startEdit(e)} className="text-[#6e1b1b] hover:text-[#431010] p-1" title="बदलें">
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button data-testid={`special-remove-${e.id}`} onClick={() => remove(e.id)} className="text-[#6e1b1b] hover:text-[#431010] p-1">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
          {!entries.length && (
            <div className="text-center text-[#9a7a5a] py-10 border border-dashed border-[#e3d6b6] rounded-xl">
              अभी कोई विशेष SK एंट्री नहीं। शेड्यूल जनरेट करने से पहले जोड़ें।
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
